import React, { useState, useEffect, useCallback } from 'react';
import {
    StyleSheet,
    SafeAreaView,
    View,
    TouchableOpacity,
    useWindowDimensions,
} from 'react-native';
import { useTheme, ActivityIndicator } from 'react-native-paper';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { showMessage } from 'react-native-flash-message';
import HRAdminWorkflows from '../../HRAdminEmployees/CreateEmployee/HRAdminWorkflows';
import HRAdminWorkflowSteps from './HRAdminWorkflowSteps';
import HRAdminWorkflowCriteria from './HRAdminWorkflowCriteria';
import WorkflowModal from './WorkflowModal';
import HRAdminCopyWorkflowSteps from './HRAdminCopyWorkflowSteps';
import { useWorkflowsForCompany } from '../../../../hooks/useWorkflowsForCompany';
import CustomLabel from '../../../../components/CustomLabel/CustomLabel';
import { useNavigation } from '@react-navigation/native';
import { useCompany } from '../../../../hooks/useCompany';
import { useAuthContext } from "../../../../hooks/useAuthContext";
import Swal from "sweetalert2";
import { createCommonStyles } from '../../../../styles/commonStyles';
import CustomButton from '../../../../components/CustomButton';

// Side menu options with translation keys
const SIDE_MENU_OPTIONS = [
    { key: 'Edit', label: 'common:Edit' },
    { key: 'Steps', label: 'common:Steps' },
    { key: 'Criteria', label: 'common:Criteria' },
    { key: 'CopyWorkflowSteps', label: 'common:CopyWorkflowSteps' },
];

const HRAdminWorkflowsScreen = ({
    selectedCompany,
    onSuccess,
    item,
}) => {
    const { width } = useWindowDimensions();
    const isLargeScreen = width >= 1300;
    const navigation = useNavigation();
    const { t } = useTranslation();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, isLargeScreen);
    const { userId } = useAuthContext();
    const { username } = useAuthContext();
    const [pressedButtons, setPressedButtons] = useState({});
    const [refreshFlag, setRefreshFlag] = useState(false);

    // Extract company code safely
    const companyCode = selectedCompany?.code ?? selectedCompany ?? null;

    // Custom hook to fetch workflows and categories
    const { workflows, workflowObjs, loading: isLoadingWorkflows } = useWorkflowsForCompany(companyCode, refreshFlag);

    // State for selected workflow and modal visibility
    const [selectedWorkflowId, setSelectedWorkflowId] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const { getWorkflowCategories, saveWorkflows, getEditWorkflow, hasWorkflowSteps, deleteWorkflows, copyWorkflowSteps } = useCompany();

    // Workflows default and original state for editing or resetting
    const [defaultWorkflows, setDefaultWorkflows] = useState([]);
    const [originalWorkflows, setOriginalWorkflows] = useState([]);

    // Editing states
    const [editingItem, setEditingItem] = useState(null);
    const [description, setDescription] = useState('');
    const [noApprover, setNoApprover] = useState(false);
    const [flexibleApproverMessage, setFlexibleApproverMessage] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showWorkflowSteps, setShowWorkflowSteps] = useState(false);
    const [showCriteria, setShowCriteria] = useState(false);
    const [selectedWorkflowCategory, setSelectedWorkflowCategory] = useState(null);
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [selectedWorkflowNoApprover, setSelectedWorkflowNoApprover] = useState(false);
    const [modalErrorMessage, setModalErrorMessage] = useState('');
    const [lastSelectedDescription, setLastSelectedDescription] = useState('');

    const [copyModalVisible, setCopyModalVisible] = useState(false);
    const [copyModalError, setCopyModalError] = useState('');

    const showWarningPopup = async () => {
        return Swal.fire({
            title: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:areYouSureDeleteWorkflow"
            )}</text>`,
            icon: "warning",
            showCancelButton: true,
            reverseButtons: true,
            cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:cancel"
            )}</text>`,
            cancelButtonColor: "#d3d3d3",
            confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">${t(
                "common:confirmMessage"
            )}</text>`,
            confirmButtonColor: "#A0A2FF",
        });
    };


    // useEffect regioin
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const res = await getWorkflowCategories();
                const categories = res?.data ?? [];
                const options = categories.map(cat => ({
                    label: cat.description,
                    value: String(cat.categoryId),
                }));
                setCategoryOptions(options);
            } catch (error) {
                console.error('Failed to fetch categories:', error);
                setCategoryOptions([]);
            }
        };
        fetchCategories();
    }, [companyCode]);

    useEffect(() => {
        if (item?.workflowId) {
            setDefaultWorkflows([{
                workflowID: item.workflowId,
                rowguid: uuidv4(),
                company: companyCode,
            }]);
        } else {
            setDefaultWorkflows([]);
        }
        setOriginalWorkflows([]);
    }, [companyCode, item]);

    useEffect(() => {
        if (selectedWorkflowId && workflowObjs[selectedWorkflowId]) {
            setSelectedWorkflowNoApprover(workflowObjs[selectedWorkflowId].noApprover ?? false);
        } else {
            setSelectedWorkflowNoApprover(false);
        }
    }, [selectedWorkflowId, workflowObjs]);

    useEffect(() => {
        if (selectedWorkflowId && workflowObjs[selectedWorkflowId]) {
            setLastSelectedDescription(workflowObjs[selectedWorkflowId].description || '');
            setDescription(workflowObjs[selectedWorkflowId].description || '');
        } else {
            setLastSelectedDescription('');
            setDescription('');
        }
    }, [selectedWorkflowId, workflowObjs]);

    useEffect(() => {
        if (showWorkflowSteps) {
            setPressedButtons({ 1: true }); // Steps button selected
        } else {
            setPressedButtons({}); // No button selected or reset as needed
        }
    }, [showWorkflowSteps]);

    useEffect(() => {
        if (!selectedWorkflowId) {
            // Clear edit modal / selection state so stale data isn't used
            setEditingItem(null);
            setDescription('');
            setNoApprover(false);
            setFlexibleApproverMessage('');
            setSelectedCategory(null);
            setLastSelectedDescription('');
            setDefaultWorkflows([]);
            setShowWorkflowSteps(false);
            setShowCriteria(false);
            // also clear any modal error
            setModalErrorMessage('');
        }
    }, [selectedWorkflowId]);
    //enregion useEffect 

    // Close modal and reset editing states
    const closeModal = useCallback(() => {
        setModalVisible(false);
        setEditingItem(null);
        setDescription(selectedWorkflowId ? lastSelectedDescription : '');
        setNoApprover(false);
        setFlexibleApproverMessage('');
        setSelectedCategory(null);
        if (showWorkflowSteps) {
            setPressedButtons({ 1: true });
        } else {
            setPressedButtons({});
        }
    }, [lastSelectedDescription, showWorkflowSteps, selectedWorkflowId]);


    // Open modal to Add New workflow
    const openModalForAddNew = () => {
        if (categoryOptions.length === 0) {
            showMessage({
                message: t('common:categoriesLoadingPleaseWait'),
                type: 'warning',
            });
            return;
        }
        setEditingItem(null);
        setDescription('');
        setSelectedCategory(null);
        setNoApprover(false);
        setFlexibleApproverMessage('');
        setSelectedWorkflowId(null);
        setModalVisible(true);
    };

    // Open modal to edit selected workflow
    const openModalForEdit = useCallback(async () => {
        if (!selectedWorkflowId) {
            showMessage({
                message: t('common:pleaseSelectWorkflow'),
                type: 'warning',
            });
            return;
        }
        // const workflowBasic = workflowObjs[selectedWorkflowId];
        // if (!workflowBasic) {
        //     showMessage({
        //         message: t('common:workflowNotFound'),
        //         type: 'warning',
        //     });
        //     return;
        // }
        try {
            const workflowFull = await getEditWorkflow(companyCode, selectedWorkflowId);
            if (!workflowFull) {
                showMessage({
                    message: t('common:workflowNotFound'),
                    type: 'warning',
                });
                return;
            }
            // Merge basic and full details (if needed)
            const workflowToEdit = workflowFull;

            setEditingItem(workflowToEdit);
            setDescription(workflowToEdit.description || '');
            const selectedCat = workflowToEdit.categoryId ? String(workflowToEdit.categoryId) : categoryOptions[0]?.value;
            setSelectedCategory(selectedCat);
            setNoApprover(workflowToEdit.noApprover ?? false);
            setFlexibleApproverMessage(workflowToEdit.approverMessage ?? '');
            setModalVisible(true);
        } catch (error) {
            console.error('Failed to fetch workflow details:', error);
            showMessage({ message: t('common:errorLoadingWorkflowDetails'), type: 'danger' });
        }
    }, [selectedWorkflowId, workflowObjs, t, categoryOptions, getEditWorkflow, companyCode]);

    // Handle side menu option press
    const handleSideMenuPress = useCallback((index) => {
        const newPressed = {};
        newPressed[index] = true;
        setPressedButtons(newPressed);

        const option = SIDE_MENU_OPTIONS[index];
        const selectedWorkflow = workflowObjs[selectedWorkflowId];
        switch (option.key) {
            case 'Edit':
                openModalForEdit();
                break;
            case 'Steps':
                if (!selectedWorkflowId) {
                    showMessage({
                        message: t('common:pleaseSelectWorkflow'),
                        type: 'warning',
                    });
                    return;
                }
                if (selectedWorkflowNoApprover) {
                    showMessage({
                        message: t('common:stepsNotAvailableNoApprover'),
                        type: 'warning',
                    });
                    return;
                }
                if (!selectedWorkflow) {
                    showMessage({
                        message: t('common:workflowNotFound'),
                        type: 'warning',
                    });
                    return;
                }
                setSelectedWorkflowCategory(selectedWorkflow.categoryId);
                setShowWorkflowSteps(true);
                setShowCriteria(false);
                break;
            case 'Criteria':
                if (!selectedWorkflowId) {
                    showMessage({
                        message: t('common:pleaseSelectWorkflow'),
                        type: 'warning',
                    });
                    return;
                }
                setSelectedWorkflowCategory(workflowObjs[selectedWorkflowId]?.categoryId || null);
                setShowCriteria(true);
                setShowWorkflowSteps(false);
                break;
            case 'CopyWorkflowSteps':
                if (!selectedWorkflowId) {
                    showMessage({
                        message: t('common:pleaseSelectWorkflow'),
                        type: 'warning',
                    });
                    return;
                }
                if (!selectedWorkflow) {
                    showMessage({
                        message: t('common:workflowNotFound'),
                        type: 'warning',
                    });
                    return;
                }
                setSelectedWorkflowCategory(selectedWorkflow.categoryId);
                setCopyModalVisible(true);
                setCopyModalError('');
                break;
            default:
                break;
        }
    }, [openModalForEdit, navigation, selectedWorkflowId, companyCode, t, selectedWorkflowNoApprover]);

    // Cancel changes handler
    const handleCancelChanges = () => {
        setDefaultWorkflows(originalWorkflows);
        showMessage({
            message: t('common:changesCancelled'),
            type: 'info',
            duration: 3000,
        });
    };

    // Save workflow handler (add or edit)
    const handleSaveWorkflow = useCallback(async () => {
        const isEditing = !!editingItem;
        if (isEditing && noApprover && editingItem?.workflowId) {
            const stepsExist = await hasWorkflowSteps(editingItem.workflowId);
            if (stepsExist) {
                setModalErrorMessage(t('common:errorDeleteStepsFirst'));
                return;
            }
        }
        if (!description.trim()) {
            showMessage({
                message: t('common:pleaseEnterDescription'),
                type: 'warning',
            });
            return;
        }
        if (!selectedCategory) {
            showMessage({
                message: t('common:pleaseSelectCategory'),
                type: 'warning',
            });
            return;
        }
        const workflowPayload = {
            WorkflowId: editingItem ? editingItem.workflowId : uuidv4(),
            Description: description,
            CategoryId: selectedCategory,
            NoApprover: noApprover,
            approverMessage: flexibleApproverMessage,
            Rowguid: editingItem?.rowguid || uuidv4(),
            UserID: userId,
        };
        try {
            const response = await saveWorkflows(companyCode, [workflowPayload]);
            if (response?.message === 'Workflows saved successfully.') {
                showMessage({
                    message: t('common:workflowSavedSuccessfully'),
                    type: 'success',
                });

                const updatedWorkflow = {
                    workflowID: workflowPayload.WorkflowId,
                    rowguid: workflowPayload.Rowguid,
                    company: companyCode,
                    description: workflowPayload.Description,
                    categoryId: workflowPayload.CategoryId,
                    noApprover: workflowPayload.NoApprover,
                };

                setDefaultWorkflows([updatedWorkflow]);
                setSelectedWorkflowId(workflowPayload.WorkflowId);
                if (onSuccess) onSuccess();
                setRefreshFlag(prev => !prev);
                setDescription(workflowPayload.Description);
                setSelectedWorkflowCategory(workflowPayload.CategoryId);
                if (editingItem) {
                    const wasNoApprover = editingItem.noApprover ?? false;
                    const isNoApproverNow = noApprover;
                    if (wasNoApprover === true && isNoApproverNow === false) {
                        setShowWorkflowSteps(true);
                    } else {
                        setShowWorkflowSteps(false);
                    }
                } else {
                    setShowWorkflowSteps(false);
                }
                closeModal();
            } else {
                throw new Error('Save failed');
            }
        } catch (error) {
            console.error('Save workflow error:', error);
            showMessage({
                message: t('common:errorSavingWorkflow'),
                type: 'danger',
            });
        }
    }, [
        description,
        noApprover,
        flexibleApproverMessage,
        editingItem,
        companyCode,
        onSuccess,
        t,
        closeModal,
        selectedCategory,
        setDefaultWorkflows,
        setRefreshFlag,
        userId,
        selectedWorkflowId,
        hasWorkflowSteps,
    ]);

    const handleDeleteSelectedWorkflow = async () => {
        if (!selectedWorkflowId) {
            showMessage({
                message: t('common:pleaseSelectWorkflow'),
                type: 'warning',
            });
            return;
        }
        const result = await showWarningPopup();
        if (!result.isConfirmed) return;
        try {
            await deleteWorkflows(selectedWorkflowId);
            setRefreshFlag(prev => !prev);
            setSelectedWorkflowId(null);
            showMessage({
                message: t('common:workflowDeletedSuccessfully'),
                type: 'success',
            });
        } catch (error) {
            // If error is 404, show assigned employees message
            if (error.response && error.response.status === 404) {
                showMessage({
                    message: t('common:errorDeletingWorkflow'),
                    type: 'danger',
                });
            } else {
                // Generic error message for other errors
                showMessage({
                    message: t('common:errorAssignedEmployees'),
                    type: 'danger',
                });
            }
        }
    };

    const handleConfirmCopySteps = async (workflowId) => {
        try {
            const response = await copyWorkflowSteps(
                companyCode,
                workflowId,
                companyCode,
                selectedWorkflowId,
                username
            );
            if (response.success) {
                showMessage({
                    message: t('common:workflowStepsCopiedSuccessfully'),
                    type: 'success',
                });
                setCopyModalVisible(false);
                setRefreshFlag(prev => !prev); // Refresh workflows after copy
            } else {
                setCopyModalError(response.message || t('common:errorCopyingWorkflowSteps'));
            }
        } catch (error) {
            setCopyModalError(t('common:errorCopyingWorkflowSteps'));
        }
    };

    if (isLoadingWorkflows) {
        return (
            <View style={styles.loader}>
                <ActivityIndicator animating color={colors.blue} />
            </View>
        );
    }

    return (
        <SafeAreaView style={{ flex: 1 }}>
            <View style={styles.sideMenuAndDropdownContainer}>
                {/* Top bar: side menu + buttons horizontally */}
                {/* <View style={styles.topBarContainer}> */}
                    {/* Side menu bar on the left */}
                    {/* <View style={styles.sideMenuHorizontal}>
                        {SIDE_MENU_OPTIONS.map((option, index) => (
                            <TouchableOpacity
                                key={option.key}
                                onPress={() => handleSideMenuPress(index)}
                                style={[
                                    styles.subMenuButtonHorizontal,
                                    option.key === 'Steps' && selectedWorkflowNoApprover ? { opacity: 0.5 } : null,
                                    pressedButtons[index] ? styles.selectedButton : null,
                                ]}
                            >
                                <CustomLabel
                                    value={t(option.label)}
                                    type="SUBTITLE"
                                    style={[{ color: colors.text }]}
                                />
                            </TouchableOpacity>
                        ))}
                    </View> */}

                    {/* New/Delete buttons on the right
                    <View style={styles.buttonsContainer}>
                        <CustomButton
                            text={t('common:new')}
                            onPress={openModalForAddNew}
                            type='BUTTON_ADD'
                            textTop='0'
                            sz={15}
                        />
                        <CustomButton
                            text={t('common:delete')}
                            onPress={handleDeleteSelectedWorkflow}
                            type="BUTTON_CANCEL"
                            textTop='0'
                            sz={15}
                            style={{ marginLeft: 10 }}
                        />
                    </View> */}
                {/* </View> */}
                {/* Workflows Content */}
                {showCriteria ? (
                    <HRAdminWorkflowCriteria
                        route={{ params: { description } }}
                        selectedWorkflowId={selectedWorkflowId}
                        categoryId={selectedWorkflowCategory}
                        company={companyCode}
                        onBack={() => {
                            setShowCriteria(false);
                            setPressedButtons({});
                            setRefreshFlag(prev => !prev);
                            if (selectedWorkflowId && workflowObjs[selectedWorkflowId]) {
                                const selectedWorkflow = workflowObjs[selectedWorkflowId];
                                setDefaultWorkflows([{
                                    workflowID: selectedWorkflowId,
                                    rowguid: uuidv4(),
                                    company: companyCode,
                                    description: selectedWorkflow.description,
                                    categoryId: selectedWorkflow.categoryId,
                                    noApprover: selectedWorkflow.noApprover,
                                }]);
                            } else {
                                setDefaultWorkflows([]);
                            }
                        }}
                    />
                ) : showWorkflowSteps ? (
                    <SafeAreaView style={styles.workflowStepsContainer}>
                        <HRAdminWorkflowSteps
                            route={{
                                params: {
                                    workflowId: selectedWorkflowId,
                                    companyCode: companyCode,
                                    categoryId: selectedWorkflowCategory,
                                },
                            }}
                            description={description}
                            onBack={() => {
                                setShowWorkflowSteps(false);
                                setRefreshFlag(prev => !prev);
                                if (selectedWorkflowId && workflowObjs[selectedWorkflowId]) {
                                    const selectedWorkflow = workflowObjs[selectedWorkflowId];
                                    setDefaultWorkflows([{
                                        workflowID: selectedWorkflowId,
                                        rowguid: uuidv4(),
                                        company: companyCode,
                                        description: selectedWorkflow.description,
                                        categoryId: selectedWorkflow.categoryId,
                                        noApprover: selectedWorkflow.noApprover,
                                    }]);
                                } else {
                                    setDefaultWorkflows([]);
                                }
                            }}
                        />
                    </SafeAreaView>
                ) : (
                    <View style={styles.workflowContent}>
                        <HRAdminWorkflows
                            company={{ company: selectedCompany }}
                            workflows={workflows}
                            workflowObjs={workflowObjs}
                            defaultWorkflows={defaultWorkflows}
                            isLoading={isLoadingWorkflows}
                            handleCancelChanges={handleCancelChanges}
                            onSuccess={onSuccess}
                            onAddNewClick={openModalForAddNew}
                            setSelectedWorkflowId={setSelectedWorkflowId}
                            onWorkflowSelect={(workflowId) => setSelectedWorkflowId(workflowId || null)}
                            selectedWorkflowId={selectedWorkflowId}
                            showDeleteIcon={false}
                            showCancelButton={false}
                            hideSaveButton={true}
                            refreshFlag={refreshFlag}
                            showDeleteButtonInHeader={true}
                            onDeleteSelectedWorkflow={handleDeleteSelectedWorkflow}
                            showSideMenu={true}  
                            buttonsContainerWidth="25%"
                            // Add these props here:
                            handleSideMenuPress={handleSideMenuPress}
                            pressedButtons={pressedButtons}
                            SIDE_MENU_OPTIONS={SIDE_MENU_OPTIONS}

                        />
                    </View>
                )}
                {/* Workflow Modal for Add/Edit */}
                <WorkflowModal
                    visible={modalVisible}
                    onClose={() => {
                        setModalErrorMessage('');
                        closeModal();
                    }}
                    editingItem={editingItem}
                    description={description}
                    setDescription={setDescription}
                    noApprover={noApprover}
                    setNoApprover={setNoApprover}
                    flexibleApproverMessage={flexibleApproverMessage}
                    setFlexibleApproverMessage={setFlexibleApproverMessage}
                    selectedCategory={selectedCategory}
                    categoryOptions={categoryOptions}
                    setSelectedCategory={setSelectedCategory}
                    addNewWorkflow={handleSaveWorkflow}
                    isEditing={!!editingItem}
                    errorMessage={modalErrorMessage}
                    setErrorMessage={setModalErrorMessage}
                    modalTitle={editingItem ? editingItem.description : t('common:addNewWorkflow')}
                    handleEditPress={(field, value) => {
                        switch (field) {
                            case 'description':
                                setDescription(value);
                                break;
                            case 'noApprover':
                                setNoApprover(value);
                                break;
                            case 'flexibleApproverMessage':
                                setFlexibleApproverMessage(value);
                                break;
                            case 'category':
                                setSelectedCategory(value);
                                break;
                            default:
                                break;
                        }
                    }}
                />
            </View>
            <View>
                <HRAdminCopyWorkflowSteps
                    visible={copyModalVisible}
                    onClose={() => setCopyModalVisible(false)}
                    onConfirm={handleConfirmCopySteps}
                    workflows={workflows}
                    company={companyCode}
                    workflowObjs={workflowObjs}
                    targetWorkflowId={selectedWorkflowId}
                    targetCompanyCode={companyCode}
                    selectedCategory={selectedWorkflowCategory}
                    errorMessage={copyModalError}
                    setErrorMessage={setCopyModalError}
                />
            </View>
        </SafeAreaView>
    );
};

const makeStyles = () =>
    StyleSheet.create({
        workflowContent: {
            flex: 1,
            flexDirection: 'row',
            width: '100%',
            paddingHorizontal: 10,
        },
        workflowStepsContainer: {
            flex: 1,
            flexDirection: 'row',
            width: '100%',
            paddingHorizontal: 10,
        },
        loader: {
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '4%',
        },
    });

export default HRAdminWorkflowsScreen;